import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, Mic, MicOff, Sparkles, PenLine, X, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { cleanupWOCompletionNote, completeWorkOrderFull } from "@/lib/api";
import { WindowCareCountCheck } from "@/components/tech/WindowCareCountCheck";
import { useI18n, tr } from "@/i18n";

/*
 * WOCompletionModal — one-stop close-out for the tech or PM.
 *
 * Captures:
 *   - Completion note (typed OR dictated via Web Speech API, auto-cleaned
 *     through Claude Haiku when the tech taps "Polish with AI")
 *   - Optional signature (simple canvas — tech signs with finger/stylus)
 *   - Optional logged hours for today's dispatch
 *   - Account Manager recap email (mailto prefilled when user clicks Send)
 *
 * Safe fallbacks:
 *   - Web Speech API unavailable → hide the mic button
 *   - AI cleanup fails → returns raw text (never blocks)
 *   - Signature optional — can complete without one
 */
export default function WOCompletionModal({ open, onClose, wo, dispatchId, defaultSignedBy = "", onCompleted }) {
  const { t, lang } = useI18n();
  const [note, setNote] = useState("");
  const [loggedHours, setLoggedHours] = useState("");
  const [signedBy, setSignedBy] = useState(defaultSignedBy);
  const [listening, setListening] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [recap, setRecap] = useState(null);
  const recognitionRef = useRef(null);
  const sigCanvasRef = useRef(null);
  const sigCtxRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasSig, setHasSig] = useState(false);

  // ── Web Speech recognition setup ─────────────────────────────────────────
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.continuous = true;
    r.interimResults = false;
    r.lang = lang === "es" ? "es-US" : "en-US";
    r.onresult = (e) => {
      const chunk = Array.from(e.results).slice(e.resultIndex).map(res => res[0].transcript).join(" ");
      if (chunk) setNote(prev => (prev ? `${prev} ${chunk}`.trim() : chunk.trim()));
    };
    r.onerror = () => { setListening(false); toast.error(tr("Mic error — try typing instead")); };
    r.onend = () => setListening(false);
    recognitionRef.current = r;
    return () => { try { r.stop(); } catch { /* noop */ } };
  }, [lang]);

  const toggleMic = () => {
    const r = recognitionRef.current;
    if (!r) { toast.error(t("Voice not supported in this browser")); return; }
    if (listening) { try { r.stop(); } catch { /* noop */ } setListening(false); return; }
    try { r.start(); setListening(true); } catch { setListening(false); }
  };

  const polishWithAI = async () => {
    if (!note.trim()) { toast.error(t("Type or dictate something first")); return; }
    setCleaning(true);
    try {
      const resp = await cleanupWOCompletionNote(wo.id, note);
      const cleaned = resp.data?.cleaned || note;
      setNote(cleaned);
      if (resp.data?.used_ai) toast.success(t("Polished ✨"));
      else toast.info(t("AI was unavailable — kept your original text"));
    } catch { toast.error(t("Failed to polish — kept original text")); }
    setCleaning(false);
  };

  // ── Signature canvas handlers ────────────────────────────────────────────
  const setupCanvas = (canvas) => {
    if (!canvas) return;
    sigCanvasRef.current = canvas;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    sigCtxRef.current = ctx;
  };
  const sigStart = (e) => {
    drawingRef.current = true;
    const p = getPoint(e);
    sigCtxRef.current.beginPath();
    sigCtxRef.current.moveTo(p.x, p.y);
    e.preventDefault();
  };
  const sigMove = (e) => {
    if (!drawingRef.current) return;
    const p = getPoint(e);
    sigCtxRef.current.lineTo(p.x, p.y);
    sigCtxRef.current.stroke();
    setHasSig(true);
    e.preventDefault();
  };
  const sigEnd = () => { drawingRef.current = false; };
  const getPoint = (e) => {
    const rect = sigCanvasRef.current.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };
  const clearSig = () => {
    const c = sigCanvasRef.current;
    if (c) sigCtxRef.current.clearRect(0, 0, c.width, c.height);
    setHasSig(false);
  };

  const submit = async () => {
    if (!note.trim()) { toast.error(t("Close-out note is required")); return; }
    setSubmitting(true);
    try {
      const sigData = hasSig ? sigCanvasRef.current.toDataURL("image/png") : null;
      const resp = await completeWorkOrderFull(wo.id, {
        note,
        signature_data: sigData,
        signed_by: signedBy || null,
        logged_hours: loggedHours ? parseFloat(loggedHours) : null,
        dispatch_id: dispatchId || null,
        email_recap: true,
      });
      toast.success(resp.data?.field_complete && resp.data?.status !== "completed"
        ? t("Marked field complete — the office will close out the work order")
        : t("WO marked Complete"));
      setRecap(resp.data?.recap_email || null);
      onCompleted?.(resp.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Failed to complete WO"));
    }
    setSubmitting(false);
  };

  const sendRecap = () => {
    if (!recap || !recap.to) { toast.error(t("No AM email on WO — set one first")); return; }
    const url = `mailto:${recap.to}?subject=${encodeURIComponent(recap.subject)}&body=${encodeURIComponent(recap.body)}`;
    window.location.href = url;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="wo-completion-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <CheckCircle className="w-4 h-4 text-green-600" />
            {t("Complete {wo}", { wo: wo?.wo_number || t("Work Order") })}
          </DialogTitle>
        </DialogHeader>

        {!recap ? (
          <div className="space-y-4">
            {/* Close-out note with mic + AI polish */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs text-slate-600 font-semibold">{t("Close-out Note")}</Label>
                <div className="flex items-center gap-1.5">
                  {recognitionRef.current && (
                    <Button type="button" size="sm" variant={listening ? "default" : "outline"} className={`h-7 px-2 text-[10px] ${listening ? "bg-red-600 hover:bg-red-700 text-white" : ""}`} onClick={toggleMic} data-testid="completion-mic">
                      {listening ? <><MicOff className="w-3 h-3 mr-1" />{t("Stop")}</> : <><Mic className="w-3 h-3 mr-1" />{t("Dictate")}</>}
                    </Button>
                  )}
                  <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-[10px]" onClick={polishWithAI} disabled={cleaning || !note.trim()} data-testid="completion-polish">
                    {cleaning ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1 text-amber-500" />}
                    {t("Polish")}
                  </Button>
                </div>
              </div>
              <Textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                rows={5}
                placeholder={t("What was completed on site? Any follow-ups? Parts used?")}
                className="text-sm"
                data-testid="completion-note"
              />
              {listening && <div className="text-[10px] text-red-600 mt-1 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> {t("Listening — speak naturally")}</div>}
            </div>

            {/* Hours + signed-by */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600 font-semibold">{t("Logged Hours")}</Label>
                <Input type="number" step="0.25" min="0" value={loggedHours} onChange={e => setLoggedHours(e.target.value)} placeholder={t("Today's hours")} className="h-8 text-sm mt-1" data-testid="completion-hours" />
              </div>
              <div>
                <Label className="text-xs text-slate-600 font-semibold">{t("Signed By")}</Label>
                <Input value={signedBy} onChange={e => setSignedBy(e.target.value)} placeholder={t("Your name")} className="h-8 text-sm mt-1" data-testid="completion-signed-by" />
              </div>
            </div>

            {/* Window Care pane-count correction (only on Window Care WOs) */}
            {wo?.id && <WindowCareCountCheck woId={wo.id} />}

            {/* Signature pad */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs text-slate-600 font-semibold flex items-center gap-1.5"><PenLine className="w-3 h-3" /> {t("Signature (optional)")}</Label>
                {hasSig && <button onClick={clearSig} className="text-[10px] text-slate-400 hover:text-red-500" data-testid="completion-sig-clear">{t("Clear")}</button>}
              </div>
              <canvas
                ref={setupCanvas}
                className="w-full h-32 border border-slate-200 rounded-lg bg-slate-50 touch-none cursor-crosshair"
                onMouseDown={sigStart} onMouseMove={sigMove} onMouseUp={sigEnd} onMouseLeave={sigEnd}
                onTouchStart={sigStart} onTouchMove={sigMove} onTouchEnd={sigEnd}
                data-testid="completion-signature-canvas"
              />
            </div>
          </div>
        ) : (
          // After successful completion — show the pre-filled AM recap email card
          <div className="space-y-3" data-testid="completion-recap">
            <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 shrink-0" />
              <span>{t("Work Order closed. Send the Account Manager a recap?")}</span>
            </div>
            <div className="text-xs space-y-1.5 bg-slate-50 rounded-lg p-3 border">
              <div><span className="text-slate-500">{t("To:")}</span> <span className="font-medium">{recap.to || t("(no AM email on file)")}</span></div>
              <div><span className="text-slate-500">{t("Subject:")}</span> <span className="font-medium">{recap.subject}</span></div>
              <pre className="text-[11px] text-slate-700 whitespace-pre-wrap border-t border-slate-200 pt-2 mt-2">{recap.body}</pre>
            </div>
          </div>
        )}

        <DialogFooter>
          {!recap ? (
            <>
              <Button variant="outline" onClick={onClose} data-testid="completion-cancel"><X className="w-3.5 h-3.5 mr-1" />{t("Cancel")}</Button>
              <Button onClick={submit} disabled={submitting || !note.trim()} className="bg-green-600 hover:bg-green-700 text-white" data-testid="completion-submit">
                {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />}
                {t("Mark Complete")}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => { setRecap(null); onClose(); }} data-testid="completion-close">{t("Close")}</Button>
              <Button onClick={sendRecap} disabled={!recap.to} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="completion-send-recap">
                <Mail className="w-3.5 h-3.5 mr-1" />{t("Email AM Recap")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
